import React, { useEffect, useMemo, useState } from 'react';
import Spinner from './ui/Spinner';
import CorePhotoLightbox from './CorePhotoLightbox';
import './CorePhotosGallery.css';
import {
  inferCondition,
  sortPhotosForGallery,
  enumerateThumbnailUrlCandidates,
} from '../utils/corePhotoCondition';
import { logCorePhotoImageError, logCorePhotoImageExhausted } from '../utils/corePhotoDebug';

function formatDepthCaption(p) {
  if (p.depth_from_m != null && p.depth_to_m != null) {
    return `${Number(p.depth_from_m).toFixed(2)}–${Number(p.depth_to_m).toFixed(2)} m`;
  }
  return p.file_name || '—';
}

/** Try SAS URLs in order so broken rendition paths fall back to the original blob. */
export function CorePhotoTileImage({ photo, className }) {
  const candidates = useMemo(() => enumerateThumbnailUrlCandidates(photo), [photo]);
  const [attempt, setAttempt] = useState(0);
  const [fallbackExhausted, setFallbackExhausted] = useState(false);

  useEffect(() => {
    setAttempt(0);
    setFallbackExhausted(false);
  }, [photo.id, photo.blob_path]);

  const src = candidates[attempt];

  if (!candidates.length) {
    return (
      <div
        className={className}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.68rem',
          color: '#6b7280',
        }}
      >
        No preview URL
      </div>
    );
  }

  if (fallbackExhausted || (candidates.length > 0 && !src)) {
    return (
      <div
        className={className}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.68rem',
          color: '#6b7280',
        }}
      >
        Cannot load image
      </div>
    );
  }

  return (
    <img
      className={className}
      src={src}
      alt=""
      loading="lazy"
      onError={() => {
        setAttempt((a) => {
          logCorePhotoImageError('thumbnail', photo, {
            attempt: a,
            totalCandidates: candidates.length,
            failedUrl: candidates[a],
          });
          const next = a + 1;
          if (next < candidates.length) return next;
          queueMicrotask(() => {
            setFallbackExhausted(true);
            logCorePhotoImageExhausted('thumbnail', photo, candidates);
          });
          return a;
        });
      }}
    />
  );
}

export default function CorePhotosGallery({
  holeId,
  photos,
  loading,
  errorMessage,
}) {
  const sorted = useMemo(() => sortPhotosForGallery(photos || []), [photos]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const openLightboxAt = (i) => {
    setLightboxIndex(i);
    setLightboxOpen(true);
  };

  if (!holeId) {
    return (
      <div style={{ padding: '1.25rem', color: '#9ca3af', textAlign: 'center' }}>
        Select a drill hole from the map or table to view core photos.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="core-photos-gallery" style={{ padding: '2.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem' }}>
        <Spinner />
        <span style={{ fontSize: '0.8rem', color: '#9ca3af' }}>Loading core photos…</span>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="core-photos-gallery" style={{ padding: '1.5rem', color: '#fca5a5', textAlign: 'center' }}>
        <p style={{ margin: 0 }}>Could not load core photos.</p>
        <p style={{ margin: '0.5rem 0 0', fontSize: '0.8rem', color: '#9ca3af', wordBreak: 'break-word' }}>
          {errorMessage}
        </p>
      </div>
    );
  }

  if (!sorted.length) {
    return (
      <div className="core-photos-gallery" style={{ padding: '1.75rem', color: '#9ca3af', textAlign: 'center' }}>
        No core tray images found for <strong style={{ color: '#f9be0b' }}>{holeId}</strong>.
      </div>
    );
  }

  return (
    <div className="core-photos-gallery">
      <p style={{ margin: '0 0 0.5rem', fontSize: '0.8rem', color: '#6b7280' }}>
        {sorted.length} image{sorted.length === 1 ? '' : 's'} — click a thumbnail for full resolution. Use Prev/Next or
        arrow keys inside the viewer.
      </p>
      <div className="core-photos-grid">
        {sorted.map((photo, idx) => {
          const cond = inferCondition(photo);
          return (
            <button
              type="button"
              key={`${photo.id ?? photo.blob_path}-${idx}`}
              className="core-photos-tile"
              onClick={() => openLightboxAt(idx)}
            >
              <CorePhotoTileImage photo={photo} className="core-photos-tile-thumb" />
              <div className="core-photos-tile-cap">
                <div className="core-photos-tile-cap-id">{formatDepthCaption(photo)}</div>
                <div style={{ textTransform: 'capitalize', marginTop: 2 }}>
                  {cond}
                  {photo.file_extension ? ` · ${String(photo.file_extension)}` : ''}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <CorePhotoLightbox
        photos={sorted}
        initialIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </div>
  );
}
